import {
  DEXFPriceUnitTypeCode,
  fetchDEXFProducts,
  fetchDEXFProductsForItemList,
  getFamilyPrice,
  getRegularPrice,
  Product,
  ProductGoodToKnow,
  ProductGoodToKnowTypeName,
  pricesForArticles,
  ExtendedIProduct,
  ProductType,
  NonMarketProduct,
  ProductDesign,
  KompisSettings,
  ProductCatalog,
  ImageType,
  fetchBryggaData,
  RangeCatalog,
  RangeProduct,
  RangeTemplate,
  hackKompisProductService,
  IProductMap,
  mergeCatalogs
} from '@inter-ikea-storageone/range';
import {
  DynamicProduct,
  DynamicProductCatalog,
  DynamicRangeUtils,
  Nullable,
  Size
} from '@inter-ikea-core/core';
import {
  ConfigureProductAction,
  configureProductGroups,
  getProductsSuccess,
  setArticlesInMarket
} from 'ducks/products';
import { getRange } from 'utils/queryUtils';
import { PlannerConfig } from 'rangeConfig/@types';
import { getPlannerConfig } from 'rangeConfig';
import { getQuery } from 'utils/queryUtils';
import transformAppearance from './transformAppearance';
import { transformDimensions, transformMeasures } from './transformMeasures';
import { transformImages, getImageFromMeta } from './transformProductImages';
import { capitalizeFirstLetter, dexfArticlesForRangeProducts, getLightWattage } from './utils';
import { AppDispatch } from 'ducks/configureStore';
import { IStorageOneDexfSettings, IStorageOneTranslations } from 'contexts/serviceSettings';
import { PRINT_PRODUCTS_KEY } from 'debug/PlannerDebug';
import { isProduction } from '../utils/platform';
import { IProduct, IProductGoodToKnow } from '@inter-ikea-kompis/types';

// Map with unavailable products and their corresponding replacement product
export type ReplacementProductMap = {
  [id: string]: string;
};

type MarketProductData = {
  inMarket: Product[];
  unavailable: Product[];
  validReplacementProducts: Product[];
  notInMarket: NonMarketProduct[];
  coreSpecific: NonMarketProduct[];
  catalog: ProductCatalog;
};

export type TransformProduct = {
  id: string;
  type: Nullable<string>;
  size: Size;
  comSize: Size;
  templateId: string;
  article: IProduct | ExtendedIProduct;
  parts: ExtendedIProduct[];
  meta: Record<string, any>;
};

const isMultipartAssembly = (product: DynamicProduct) =>
  isCustomDexfAssembly(product) ||
  (product.template.parts?.length && product.template.parts?.length > 1);

const isCustomDexfAssembly = (product: DynamicProduct) => product.dexf?.startsWith('ASM');
const InteriorSuggestionProductTypes = [
  ProductType.PAX_INTERIOR_SUGGESTIONS,
  ProductType.PLATSA_INTERIOR_SUGGESTION,
  ProductType.SMASTAD_INTERIOR_SUGGESTION
];

const getDexfFromItemList = () =>
  getQuery().get('enableAllProducts') === 'true' ||
  getQuery().get('generateInteriorAssembly') === 'true' ||
  getQuery().get('generateFrontAssembly') === 'true';

const transformPrice = (article: IProduct | ExtendedIProduct) => {
  if (!article.content?.priceInformation) {
    return {
      hasFamilyPrice: false,
      currentPrice: 0,
      regularPrice: 0,
      nbrOfItemsInPackage: 1
    };
  }
  const regularPrice = getRegularPrice(article);
  const familyPrice = getFamilyPrice(article);

  const nbrOfItemsInPackage =
    article.content?.priceUnit?.find(
      (unit: any) => unit.typeCode === DEXFPriceUnitTypeCode.packageQuantity
    )?.value || 1;

  const nbrOfItemsInPackageWithText =
    article.content?.priceUnit?.find(
      (unit: any) => unit.typeCode === DEXFPriceUnitTypeCode.packageQuantity
    )?.text || '';

  const hasFamilyPrice = !!familyPrice;

  return {
    hasFamilyPrice,
    currentPrice: hasFamilyPrice ? familyPrice : regularPrice,
    regularPrice,
    nbrOfItemsInPackage,
    nbrOfItemsInPackageWithText
  };
};

const transformGoodToKnow = (article: IProduct | ExtendedIProduct): ProductGoodToKnow[] => {
  const goodToKnow: IProductGoodToKnow[] = article?.content?.goodToKnow || [];

  return goodToKnow.map(
    (gtk): ProductGoodToKnow => {
      return {
        typeName: (gtk.typeName as string) as ProductGoodToKnowTypeName,
        text: gtk.text
      };
    }
  );
};

const checkForUnvaildParts = (parts: ExtendedIProduct[]): boolean => {
  return parts.find(p => !p.valid) ? true : false;
};

const getProductImages = (product: TransformProduct, assetPath: string) => {
  const images = transformImages(product.article?.content?.image || []);

  if (product.meta?.image && assetPath) {
    const metaImages = getImageFromMeta(product.meta.image, assetPath, product.type as ProductType);
    const filteredOriginalImages = images.filter(image => image.type !== ImageType.MPP);
    const mergedImages = [...metaImages, ...filteredOriginalImages];
    return mergedImages;
  }

  return images;
};

const getCommunicatedSizeText = (
  product: IProduct | ExtendedIProduct,
  dexfSettings: IStorageOneDexfSettings
) => {
  return dexfSettings?.localisation.useMetricMeasures
    ? product.content?.measureReference?.textMetric
    : product.content?.measureReference?.textImperial;
};

const getPartSpec = (product: TransformProduct, dexfSettings: IStorageOneDexfSettings) => {
  return (part: ExtendedIProduct) => ({
    id: part.content?.itemNoGlobal || part.content?.ruItemNo || '',
    localId: part.content?.ruItemNo || part.content?.itemNoGlobal || '',
    itemType: part.content?.ruItemType || part.content?.itemType || '',
    complimentary: part.complimentary || false,
    product:
      part.content?.itemNoGlobal === product.id
        ? null
        : {
            goodToKnow: transformGoodToKnow(part),
            priceInformation: transformPrice(part),
            typeName: capitalizeFirstLetter(part.content?.typeName || ''),
            type: part.type,
            designText: part.content?.validDesignText || '',
            coreProductId: part.content?.itemNoGlobal || part.content?.ruItemNo || '',
            communicatedDimensions: transformDimensions(part.communicatedDimensions),
            name: part.content?.name || '',
            images: part.content?.image ? transformImages(part.content?.image) : [],
            ruItemNo: part.content?.ruItemNo || '',
            communicatedSizeText: getCommunicatedSizeText(part, dexfSettings),
            valid: part.valid
          }
  });
};

const transformProductData = (
  product: TransformProduct,
  assetPath: string,
  dexfSettings: IStorageOneDexfSettings,
  translations: IStorageOneTranslations
): Product => {
  if (!product.article.content) {
    throw new Error('Product content is missing');
  }
  const measurements = transformMeasures(product, dexfSettings, translations);
  return {
    valid: !checkForUnvaildParts(product.parts),
    goodToKnow: transformGoodToKnow(product.article),
    ruItemNo: product.article.content.ruItemNo,
    coreProductId: product.id,
    type: product.type,
    typeName: capitalizeFirstLetter(product.article?.content.typeName || ''),
    name: product?.article?.content.name,
    design: product.meta?.design || '',
    designText: product.article.content.validDesignText,
    templateId: product.templateId,
    communicatedSizeText: getCommunicatedSizeText(product.article, dexfSettings),
    images: getProductImages(product, assetPath),
    dimensions: measurements.dimensions,
    communicatedDimensions: measurements.communicatedDimensions,
    communicatedDimensionsLocalized: measurements.communicatedDimensionsLocalized,
    complainceInformation: product.article.content.complianceInformation,
    globalItemNo: product.article.itemId,
    priceInformation: transformPrice(product.article),
    appearances: transformAppearance(product.article?.content.filterAttribute || []).sort(
      (a: any, b: any) => b.id - a.id
    ),
    meta: product.meta,
    parts: product.parts.map(getPartSpec(product, dexfSettings)),
    wattage: getLightWattage(product.article?.content.measure),
    reason: product.article.reason,
    content: product.article.content
  } as Product;
};

const getRangeProductsInAssembly = (
  assembly: DynamicProduct,
  rangeProducts: DynamicProduct[]
): DynamicProduct[] => {
  const partsInAssembly = assembly.template.parts ? assembly.template.parts : [];
  const rangeProductsInAssembly = partsInAssembly.reduce((acc: any, part: any) => {
    const rp = rangeProducts.find(rp => rp.id === part.id) as DynamicProduct;
    if (!rp.dexf && rp.template.parts) {
      // Handle if assembly part is an assembly itself
      const partProducts = getRangeProductsInAssembly(rp, rangeProducts);
      return [...acc, ...partProducts];
    }
    if (rp?.dexf || part.included) {
      return [...acc, { ...rp, included: part.included, main: part.main }];
    }
    return acc;
  }, []);
  return rangeProductsInAssembly;
};

export const createSingleArticleProduct = (
  product: DynamicProduct,
  dexfArticle: IProduct,
  assetPath: string,
  dexfSettings: IStorageOneDexfSettings,
  translations: IStorageOneTranslations
): Product | null => {
  // Use package quantity from range product if exist
  const priceUnit = product.partsInPackage
    ? [
        {
          typeCode: DEXFPriceUnitTypeCode.packageQuantity,
          value: product.partsInPackage,
          text: dexfArticle.content?.priceUnit ? dexfArticle.content.priceUnit[0].text : ''
        } as any
      ]
    : dexfArticle.content?.priceUnit || null;
  const article = {
    ...dexfArticle,
    type: product.type,
    complimentary: false,
    communicatedDimensions: product.template.comSize,
    content: { ...dexfArticle.content!, priceUnit }
  };

  return transformProductData(
    {
      id: product.id,
      type: product.type,
      size: product.template.size,
      templateId: product.template.id,
      comSize: product.template.comSize,
      article: article,
      parts: [article],
      meta: DynamicRangeUtils.getMeta(product)
    },
    assetPath,
    dexfSettings,
    translations
  );
};

export const createAssemblyProduct = (
  assembly: DynamicProduct,
  rangeProducts: DynamicProduct[],
  dexfArticleMap: IProductMap,
  assetPath: string,
  dexfSettings: IStorageOneDexfSettings,
  translations: IStorageOneTranslations
): Product | null => {
  const rangeProductsInAssembly = getRangeProductsInAssembly(assembly, rangeProducts);
  const allDEXFArticlesInAssembly = rangeProductsInAssembly.every(rp => !rp.dexf)
    ? dexfArticlesForRangeProducts([assembly], dexfArticleMap)
    : dexfArticlesForRangeProducts(rangeProductsInAssembly, dexfArticleMap);
  const mainProductInAssembly = assembly.dexf
    ? assembly
    : rangeProductsInAssembly.find((rp: any) => rp.main) || rangeProductsInAssembly[0];

  if (!mainProductInAssembly || !mainProductInAssembly.dexf) {
    return null;
  }
  const mainDEXFArticleInAssembly = { ...dexfArticleMap[mainProductInAssembly?.dexf] };

  if (
    Object.keys(mainDEXFArticleInAssembly).length === 0 ||
    !allDEXFArticlesInAssembly.every(Boolean)
  ) {
    return null;
  }

  const transformedProduct = transformProductData(
    {
      id: assembly.id,
      type: assembly.type,
      size: assembly.template.size,
      comSize: assembly.template.comSize,
      templateId: assembly.template.id,
      article: mainDEXFArticleInAssembly,
      parts: allDEXFArticlesInAssembly.filter(Boolean) as ExtendedIProduct[],
      meta: DynamicRangeUtils.getMeta(assembly)
    },
    assetPath,
    dexfSettings,
    translations
  );

  const pricesForProducts = pricesForArticles(allDEXFArticlesInAssembly as ExtendedIProduct[]);

  return {
    ...transformedProduct,
    priceInformation: {
      ...transformedProduct.priceInformation,
      ...pricesForProducts
    }
  };
};

const createNonMarketProduct = (product: DynamicProduct): NonMarketProduct => {
  return {
    coreProductId: product.id,
    globalItemNo: '',
    dimensions: transformDimensions(product.template.size),
    communicatedDimensions: transformDimensions(product.template.comSize),
    type: product.type as ProductType,
    meta: DynamicRangeUtils.getMeta(product),
    design: (DynamicRangeUtils.getMeta(product)?.design || '') as ProductDesign
  };
};

/**
 * Get DEXF product ids for all products in range
 */
const getProductNbrsFromCatalogs = (productCatalogs: RangeCatalog[]) => {
  const productNbrs = productCatalogs.flatMap(catalog => {
    return catalog.products
      .map(product => (product.dexf && product.dexf !== '' ? product.dexf : null))
      .filter(Boolean) as string[];
  });
  return [...new Set(productNbrs)];
};

/**
 * We need to remove autoAdaptGroup for products not sold in market
 * so that Core doesn't autoadapt to that product
 */
const removeAutoAdaptForUnavailableProducts = (
  catalog: RangeCatalog,
  productsInMarket: Product[]
): RangeCatalog => {
  const filteredProducts =
    productsInMarket.length === 0
      ? catalog.products
      : catalog.products.map((product: RangeProduct) => {
          if (!productsInMarket.find(p => p.coreProductId === product.id)) {
            // If product isn't sold in the market it should not be possible to auto-adapt to the product
            return { ...product, autoAdaptGroup: null };
          }
          return product;
        });
  return { ...catalog, products: filteredProducts };
};

/**
 * Checks if there is a brygga change package specified in query parameters and
 * in that case fetches catalog and template from that change package.
 */
const getBryggaData = async (productCatalog: ProductCatalog, experienceName: string) => {
  const bryggaData = await fetchBryggaData(productCatalog, experienceName);
  if (bryggaData) {
    return {
      ...productCatalog,
      catalog: bryggaData.catalog as RangeCatalog,
      template: bryggaData.template as RangeTemplate
    };
  }
  return productCatalog;
};

/**
 * If an interior suggestion includes products that is not sold in market but that has a
 * replacement product that is sold we replace that part in the interior suggestion for the replacement product
 */
const replaceUnavailableProductsInInteriorSuggestions = (
  interiorSuggestionCatalog: RangeCatalog,
  allCatalogs: RangeCatalog[],
  articleMap: IProductMap
): RangeCatalog => {
  const allProductsCatalog = mergeCatalogs(allCatalogs);
  const replacementProductMap = getReplacementProductMap(allProductsCatalog, articleMap);
  const products = interiorSuggestionCatalog.products.map((product: RangeProduct) => {
    if (product.type && InteriorSuggestionProductTypes.includes(product.type as ProductType)) {
      const parts = product.template.parts?.map(part => {
        const replacementProduct = replacementProductMap[part.id];
        return replacementProduct ? { ...part, id: replacementProduct } : part;
      });
      return { ...product, template: { ...product.template, parts } };
    } else {
      return product;
    }
  });
  return { ...interiorSuggestionCatalog, products };
};

/**
 * Returns a map with all unavailable products that has a available replacement product
 */
const getReplacementProductMap = (catalog: RangeCatalog, articleMap: IProductMap) => {
  const unavailableProducts = catalog.products.filter((product: RangeProduct) => {
    return product.dexf && product.dexf !== '' && articleMap[product.dexf]?.valid === false;
  });
  return unavailableProducts.reduce((acc: ReplacementProductMap, product: RangeProduct) => {
    const replacementProduct = product.meta?.replacedBy;
    if (replacementProduct && !unavailableProducts.find(p => p.id === replacementProduct)) {
      acc[product.id] = replacementProduct;
    }
    return acc;
  }, {});
};

/**
 * If product has dexfAlias and not dexf we set the dexf field as the
 * dexfAlias value.
 */
const replaceDexfIfDexfAlias = (catalog: RangeCatalog) => {
  const products = catalog.products.map((product: RangeProduct) => {
    if (
      (!product.dexf || product.dexf === '') &&
      product.template.dexfAlias &&
      product.template.dexfAlias !== ''
    ) {
      return { ...product, dexf: product.template.dexfAlias };
    }
    return product;
  });
  return { ...catalog, products };
};

export function getPrintProductIds(): string[] {
  const data = sessionStorage.getItem(PRINT_PRODUCTS_KEY);
  const ids = data ? JSON.parse(data) : [];
  return ids;
}

export const fetchProducts = async (
  settings: KompisSettings,
  dexfSettings: IStorageOneDexfSettings,
  translations: IStorageOneTranslations,
  plannerConfig: PlannerConfig,
  dispatch: AppDispatch
): Promise<PlannerConfig> => {
  if (!isProduction()) {
    await hackKompisProductService();
  }

  const articles = getDexfFromItemList()
    ? await fetchDEXFProductsForItemList(
        settings,
        getProductNbrsFromCatalogs(plannerConfig.productCatalogs.map(c => c.catalog))
      )
    : await fetchDEXFProducts(settings);

  const articleMap = articles.reduce((acc: IProductMap, p: IProduct) => {
    acc[p.itemId] = p;
    return acc;
  }, {});

  // Check if we should load catalog from Brygga
  let productCatalogs = await Promise.all(
    plannerConfig.productCatalogs.map(async productCatalog => {
      return await getBryggaData(productCatalog, settings.translationPrefixApplication);
    })
  );

  productCatalogs = productCatalogs.map(productCatalog => {
    return {
      ...productCatalog,
      catalog: replaceDexfIfDexfAlias(productCatalog.catalog)
    };
  });

  const allRangeProducts = productCatalogs.flatMap(catalog => {
    const dynamicCatalog = new DynamicProductCatalog(
      JSON.stringify(catalog.catalog),
      JSON.stringify(catalog.template),
      catalog.assetsPath
    );
    return dynamicCatalog.productDTOs();
  });

  const productData: MarketProductData[] = productCatalogs.map(productCatalog => {
    let modifiedRangeCatalog = productCatalog.catalog;
    if (productCatalog.name.includes('interior-suggestion')) {
      modifiedRangeCatalog = replaceUnavailableProductsInInteriorSuggestions(
        productCatalog.catalog,
        productCatalogs.map(c => c.catalog),
        articleMap
      );
    }

    const dynamicProductCatalog = new DynamicProductCatalog(
      JSON.stringify(modifiedRangeCatalog),
      JSON.stringify(productCatalog.template),
      productCatalog.assetsPath
    );

    const rangeProducts = dynamicProductCatalog.productDTOs();
    const productsNotInMarket: NonMarketProduct[] = [];
    const coreSpecificProducts: NonMarketProduct[] = [];

    let transformedProducts = rangeProducts.reduce((products: Product[], rp: DynamicProduct) => {
      const dexfArticle = articleMap[rp.dexf!];
      let product;

      if (isMultipartAssembly(rp)) {
        product = createAssemblyProduct(
          rp,
          allRangeProducts,
          articleMap,
          productCatalog.assetsPath,
          dexfSettings,
          translations
        );
      } else if (dexfArticle) {
        product = createSingleArticleProduct(
          rp,
          dexfArticle,
          productCatalog.assetsPath,
          dexfSettings,
          translations
        );
      } else {
        const nonMarketProduct = createNonMarketProduct(rp);
        if (rp.dexf === '') {
          coreSpecificProducts.push(nonMarketProduct);
        } else {
          productsNotInMarket.push(nonMarketProduct);
        }
      }
      if (getPrintProductIds().includes(rp.id)) {
        // eslint-disable-next-line no-console
        console.log('Range Product', rp);
        // eslint-disable-next-line no-console
        console.log('Planner Product', product);
      }

      if (product) {
        products.push(product);
      }
      return products;
    }, []);

    // Get all replacement product ids for valid products (specified by replacedBy in old product meta data)
    const replacableProductIds = transformedProducts
      .filter(p => p.valid && p.meta?.replacedBy)
      .map(p => p.meta?.replacedBy);
    // If market sells both old and new replacement product we add the new products to a different list in the product state
    // and remove them from the productsInMarket list so that they will not be displayed in the planner
    const validReplacementProductsWithValidOldProducts = transformedProducts.filter(
      p => p.valid && replacableProductIds.includes(p.coreProductId)
    );
    transformedProducts = transformedProducts.filter(
      p =>
        !validReplacementProductsWithValidOldProducts
          .map(rp => rp.coreProductId)
          .includes(p.coreProductId)
    );

    const productTransformers = getPlannerConfig(getRange()).productTransformers || [];
    transformedProducts = productTransformers.reduce(
      (acc, productTransformer) => productTransformer(acc, articles),
      transformedProducts
    );

    const unavailableProducts = transformedProducts.filter(p => !p.valid);
    const productsInMarket = transformedProducts.filter(p => p.valid);

    modifiedRangeCatalog = removeAutoAdaptForUnavailableProducts(
      modifiedRangeCatalog,
      productsInMarket
    );

    return {
      inMarket: productsInMarket,
      unavailable: unavailableProducts,
      validReplacementProducts: validReplacementProductsWithValidOldProducts,
      notInMarket: productsNotInMarket,
      coreSpecific: coreSpecificProducts,
      catalog: { ...productCatalog, catalog: modifiedRangeCatalog }
    };
  });
  const inMarket = productData.flatMap(data => data.inMarket);

  if (!inMarket.length) {
    throw new Error('No products in market');
  }

  const replacementProducts = productData.flatMap(data => data.validReplacementProducts);
  const unavailableProducts = productData.flatMap(data => data.unavailable);
  const productsNotInMarket = productData.flatMap(data => data.notInMarket);
  const coreSpecificProducts = productData.flatMap(data => data.coreSpecific);

  const productsInMarketIds = inMarket.map(product => product.coreProductId);
  const coreSpecificProductIds = coreSpecificProducts.map(product => product.coreProductId);

  dispatch(setArticlesInMarket([...new Set([...productsInMarketIds, ...coreSpecificProductIds])]));

  dispatch(
    getProductsSuccess({
      inMarket,
      replacementProducts,
      unavailableProducts,
      productsNotInMarket,
      coreSpecificProducts
    })
  );

  dispatch(
    configureProductGroups(
      plannerConfig.productGroups.reduce((acc, pg) => {
        const availableProducts = inMarket
          .filter(product => pg.productTypes.includes(product.type))
          .map(p => p.coreProductId);

        acc[pg.id] = {
          all: availableProducts,
          filtered: availableProducts,
          toggled: availableProducts
        };

        return acc;
      }, {} as ConfigureProductAction)
    )
  );

  return { ...plannerConfig, productCatalogs: productData.map(data => data.catalog) };
};
